import React, { useEffect, useRef, useState } from 'react';
import ExpandMoreOutlinedIcon from '@mui/icons-material/ExpandMoreOutlined';

interface Option {
  value: string;
  label: string;
}

interface Props {
  options: Option[];
  value: string;
  onChange: (value: string) => void;
  label?: string;
  icon?: React.ReactNode;
  placeholder?: string;
  disabled?: boolean;
  /** Render the menu in normal flow (see LensMultiSelect / .eh-inline-menu). */
  inline?: boolean;
  /** Extra class on the root field (e.g. to control flex sizing in a row). */
  className?: string;
}

/**
 * Single-select styled to match the Export Screening lens dropdown
 * (`entity-screening-parameter-*` classes) so the screening-settings controls
 * read as one family. Closes on selection; no search box (short option lists).
 */
const StyledSingleSelect: React.FC<Props> = ({
  options,
  value,
  onChange,
  label,
  icon,
  placeholder = 'Select…',
  disabled = false,
  inline = false,
  className,
}) => {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!open) return undefined;
    const handlePointerDown = (event: MouseEvent) => {
      if (ref.current && !ref.current.contains(event.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handlePointerDown);
    return () => document.removeEventListener('mousedown', handlePointerDown);
  }, [open]);

  const selectedLabel =
    options.find((o) => o.value === value)?.label ?? placeholder;

  const pick = (v: string) => {
    onChange(v);
    setOpen(false);
  };

  return (
    <div
      className={`entity-screening-parameter-field${open ? ' active' : ''}${
        className ? ` ${className}` : ''
      }`}
      ref={ref}
    >
      {label && (
        <label>
          {icon}
          {label}
        </label>
      )}
      <button
        type='button'
        className={`entity-screening-parameter-select${open ? ' active' : ''}`}
        onClick={() => !disabled && setOpen((o) => !o)}
        disabled={disabled}
      >
        <span>{selectedLabel}</span>
        <ExpandMoreOutlinedIcon style={{ fontSize: 22 }} />
      </button>
      {open && (
        <div
          className={`entity-screening-parameter-menu${
            inline ? ' eh-inline-menu' : ''
          }`}
        >
          <div className='entity-screening-parameter-options'>
            {options.map((o) => {
              const isSelected = o.value === value;
              return (
                <button
                  type='button'
                  key={o.value || '__default__'}
                  className='entity-screening-parameter-option'
                  onClick={() => pick(o.value)}
                >
                  <span
                    className={`entity-screening-parameter-check${
                      isSelected ? ' selected' : ''
                    }`}
                  />
                  <span>{o.label}</span>
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default StyledSingleSelect;
